import { api } from '../lib/api-client.js';

import type {
  BulkMarkFrictionCleanInput,
  CreateLeadWorkEventInput,
  ExemptionStatus,
  FrictionStatus,
  LeadStatus,
  TestResult,
  UpdateLeadWorkEventInput,
  UpsertUnitLeadStatusInput,
} from '@sitewise/shared';

/**
 * Frontend wrappers for the Migration 2 lead-tracker endpoints.
 * Mirrors the per-unit / per-building / org-wide split on the API
 * side; consumers are the unit Lead tab, the building Lead tab, and
 * the org-wide `/lead` page.
 */

// ---------- Shared shapes ----------

export interface LeadStatusRow {
  frictionStatus: FrictionStatus;
  frictionStatusUpdatedAt: string | null;
  exemptionStatus: ExemptionStatus;
  exemptionStatusUpdatedAt: string | null;
  exemptionLetterDocumentId: string | null;
  testResult: TestResult;
  positiveCount: number | null;
  latestXrfDate: string | null;
  latestXrfReportDocumentId: string | null;
  notes: string | null;
  updatedAt: string | null;
  exists: boolean;
}

export interface LeadAttachedDocument {
  id: string;
  kind: string;
  originalFilename: string;
  contentType: string;
  bytes: number;
  caption: string | null;
  createdAt: string;
}

export interface UnitLeadState {
  unitId: string;
  buildingId: string;
  leadStatus: LeadStatus;
  leadStatusUpdatedAt: string | null;
  status: LeadStatusRow;
  exemptionLetter: LeadAttachedDocument | null;
  latestXrfReport: LeadAttachedDocument | null;
  workEvents: LeadWorkEvent[];
}

export interface LeadWorkEventDocument {
  id: string;
  kind: string;
  originalFilename: string;
  contentType: string;
  bytes: number;
}

export interface LeadWorkEvent {
  id: string;
  unitId: string;
  performedOn: string;
  description: string;
  contractor: string | null;
  documentIds: string[];
  documents: LeadWorkEventDocument[];
  createdByUserId: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LeadHistoryEntry {
  id: string;
  unitId: string;
  changedAt: string;
  changedByUserId: string | null;
  changedByName: string | null;
  changedByEmail: string | null;
  changeNote: string | null;
  changedFields: string[];
  prior: {
    frictionStatus: string | null;
    exemptionStatus: string | null;
    exemptionLetterDocumentId: string | null;
    testResult: string | null;
    positiveCount: number | null;
    latestXrfDate: string | null;
    latestXrfReportDocumentId: string | null;
    notes: string | null;
  };
}

export interface UpsertUnitLeadResult {
  state: UnitLeadState;
  historyId: string | null;
  changedFields: string[];
  priorLeadStatus: LeadStatus | null;
}

// ---------- Per-unit ----------

export async function fetchUnitLead(unitId: string): Promise<UnitLeadState> {
  return api(`/units/${unitId}/lead`);
}

export async function updateUnitLead(
  unitId: string,
  patch: UpsertUnitLeadStatusInput,
): Promise<UpsertUnitLeadResult> {
  return api(`/units/${unitId}/lead`, { method: 'PUT', body: patch });
}

export async function fetchUnitLeadHistory(
  unitId: string,
  limit = 50,
): Promise<{ entries: LeadHistoryEntry[] }> {
  return api(`/units/${unitId}/lead/history?limit=${limit}`);
}

export async function createUnitLeadWorkEvent(
  unitId: string,
  input: CreateLeadWorkEventInput,
): Promise<{ event: LeadWorkEvent }> {
  return api(`/units/${unitId}/lead/work-events`, {
    method: 'POST',
    body: input,
  });
}

export async function updateUnitLeadWorkEvent(
  unitId: string,
  eventId: string,
  input: UpdateLeadWorkEventInput,
): Promise<{ event: LeadWorkEvent }> {
  return api(`/units/${unitId}/lead/work-events/${eventId}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function deleteUnitLeadWorkEvent(
  unitId: string,
  eventId: string,
): Promise<void> {
  await api(`/units/${unitId}/lead/work-events/${eventId}`, { method: 'DELETE' });
}

// ---------- Per-building ----------

export interface BuildingLeadGridCounts {
  total: number;
  untested: number;
  testedNegative: number;
  testedPositive: number;
  exemptApproved: number;
  exemptPending: number;
  exemptRejected: number;
  frictionCleanOnly: number;
  unknown: number;
}

export interface BuildingLeadGridRow {
  unitId: string;
  unitNumber: string;
  floor: string | null;
  leadStatus: LeadStatus;
  leadStatusUpdatedAt: string | null;
  frictionStatus: FrictionStatus;
  exemptionStatus: ExemptionStatus;
  exemptionLetterDocumentId: string | null;
  testResult: TestResult;
  positiveCount: number | null;
  latestXrfDate: string | null;
  latestXrfReportDocumentId: string | null;
  hasLeadRow: boolean;
}

export interface BuildingLeadGridResponse {
  building: { id: string; name: string };
  counts: BuildingLeadGridCounts;
  rows: BuildingLeadGridRow[];
}

export interface BuildingLeadFilters {
  friction_status?: FrictionStatus[];
  exemption_status?: ExemptionStatus[];
  test_result?: TestResult[];
  has_positives?: boolean;
  untested?: boolean;
  friction_clean?: boolean;
  q?: string;
}

function buildLeadQueryString(f: BuildingLeadFilters): string {
  const params = new URLSearchParams();
  if (f.friction_status && f.friction_status.length > 0) {
    params.set('friction_status', f.friction_status.join(','));
  }
  if (f.exemption_status && f.exemption_status.length > 0) {
    params.set('exemption_status', f.exemption_status.join(','));
  }
  if (f.test_result && f.test_result.length > 0) {
    params.set('test_result', f.test_result.join(','));
  }
  if (f.has_positives !== undefined) params.set('has_positives', String(f.has_positives));
  if (f.untested !== undefined) params.set('untested', String(f.untested));
  if (f.friction_clean !== undefined) params.set('friction_clean', String(f.friction_clean));
  if (f.q) params.set('q', f.q);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export async function fetchBuildingLeadGrid(
  buildingId: string,
  filters: BuildingLeadFilters = {},
): Promise<BuildingLeadGridResponse> {
  return api(`/buildings/${buildingId}/lead${buildLeadQueryString(filters)}`);
}

export interface BulkMarkFrictionCleanResult {
  updated: number;
  skipped: string[];
  notFound: string[];
}

export async function bulkMarkFrictionClean(
  buildingId: string,
  input: BulkMarkFrictionCleanInput,
): Promise<BulkMarkFrictionCleanResult> {
  return api(`/buildings/${buildingId}/lead/bulk-mark-friction-clean`, {
    method: 'POST',
    body: input,
  });
}

// ---------- Org-wide ----------

export interface LeadRollupCounts {
  total: number;
  untested: number;
  testedNegative: number;
  testedPositive: number;
  exemptApproved: number;
  exemptPending: number;
  exemptRejected: number;
  frictionCleanOnly: number;
  unknown: number;
}

export interface LeadRollupBuildingRow {
  buildingId: string;
  buildingName: string;
  counts: LeadRollupCounts;
  lastUpdatedAt: string | null;
}

export interface LeadRollupUnitRow {
  unitId: string;
  unitNumber: string;
  buildingId: string;
  buildingName: string;
  leadStatus: LeadStatus;
  positiveCount: number | null;
  latestXrfDate: string | null;
  testResult: TestResult;
}

export interface LeadRollupResponse {
  counts: LeadRollupCounts;
  perBuilding: LeadRollupBuildingRow[];
  topPositives: LeadRollupUnitRow[];
  recentlyTested: LeadRollupUnitRow[];
}

export async function fetchLeadRollup(
  buildingIds?: string[],
): Promise<LeadRollupResponse> {
  const qs = buildingIds && buildingIds.length > 0
    ? `?building_ids=${encodeURIComponent(buildingIds.join(','))}`
    : '';
  return api(`/lead/rollup${qs}`);
}

export interface LeadUnitsListResponse {
  rows: LeadRollupUnitRow[];
  nextCursor: string | null;
}

export interface LeadUnitsListFilters extends BuildingLeadFilters {
  building_id?: string;
  limit?: number;
}

export async function fetchLeadUnits(
  filters: LeadUnitsListFilters = {},
): Promise<LeadUnitsListResponse> {
  const base = buildLeadQueryString(filters);
  const params = new URLSearchParams(base.startsWith('?') ? base.slice(1) : base);
  if (filters.building_id) params.set('building_id', filters.building_id);
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return api(`/lead/units${qs ? `?${qs}` : ''}`);
}
